import { MOMENTS_COACH_FAQS_URL } from '@shared/constants';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@shared/ui';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import type {
  AdminAppointment,
  AdminAppointmentSchedulingContext,
  AdminCalendarConflict,
  AppointmentKind,
} from '../appointments/types';
import {
  type AdminAppointmentErrorGuidance,
  adminAppointmentErrorGuidance,
  adminAppointmentErrorMessage,
  ENGAGEMENT_SCHEDULING_GUIDANCE,
  engagementSchedulingGuidance,
  isAdminAppointmentTerminalRejection,
} from './adminAppointmentErrors';
import {
  useBookAdminAppointmentMutation,
  useGetAdminAppointmentSchedulingContextQuery,
} from './api/adminAppointmentsApi';
import { useAdminAppointmentOperation } from './useAdminAppointmentOperation';
import { useStableIdempotencyKey } from './useStableIdempotencyKey';

interface AdminAppointmentBookingDialogProps {
  open: boolean;
  kind: AppointmentKind;
  coachPersonId: string;
  coachDisplayName?: string;
  memberPersonId: string;
  viewerTimezone: string;
  onClose: () => void;
  onComplete: () => void;
}

export function AdminAppointmentBookingDialog({
  open,
  kind,
  coachPersonId,
  memberPersonId,
  viewerTimezone,
  onClose,
  onComplete,
}: AdminAppointmentBookingDialogProps) {
  const adminPrincipalId = useAppSelector((state) => {
    const principalId = state.auth.user?.id;
    if (!principalId) throw new Error('authenticated admin principal is required');
    return principalId;
  });
  const [visibleWeeks, setVisibleWeeks] = useState<1 | 2 | 3>(1);
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [pendingAppointmentIds, setPendingAppointmentIds] = useState<string[]>([]);
  const [confirmedComplete, setConfirmedComplete] = useState(false);
  const [calendarConflictConfirmation, setCalendarConflictConfirmation] = useState({
    identity: '',
    confirmed: false,
  });
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorGuidance, setErrorGuidance] = useState<AdminAppointmentErrorGuidance | null>(null);
  const [requestUnresolved, setRequestUnresolved] = useState(false);
  const [confirmedAppointment, setConfirmedAppointment] = useState<AdminAppointment | null>(null);
  const submitting = useRef(false);
  const completedOperation = useRef<string | null>(null);
  const idempotency = useStableIdempotencyKey('admin-book', adminPrincipalId);
  const contextQuery = useGetAdminAppointmentSchedulingContextQuery({
    kind,
    coach_person_id: coachPersonId,
    member_person_id: memberPersonId,
    viewer_timezone: viewerTimezone,
    visible_weeks: visibleWeeks,
    intent: 'book',
  });
  const [book, mutation] = useBookAdminAppointmentMutation();
  const operationQuery = useAdminAppointmentOperation(operationId, pendingAppointmentIds);
  const context = contextQuery.data;
  const operation = operationQuery.data;
  const contextUnavailable = !contextQuery.isLoading && (Boolean(contextQuery.error) || !context);
  const contextErrorGuidance = adminAppointmentErrorGuidance(contextQuery.error);
  const schedulingGuard =
    kind === 'coaching' && !contextErrorGuidance
      ? engagementSchedulingGuard(
          context?.eligibility,
          contextUnavailable || Boolean(context && !hasValidCoachingAvailability(context))
        )
      : null;
  const slots =
    context?.availability.days.flatMap((day) =>
      day.slots.map((slot) => ({ ...slot, appointmentCount: day.appointment_count }))
    ) ?? [];
  const selectedSlot = slots.find((slot) => slot.start_at === selectedStart);
  const selectedCalendarConflict =
    kind === 'coaching' ? selectedSlot?.calendar_conflict : undefined;
  const selectedAppointmentCount = selectedSlot?.appointmentCount ?? undefined;
  const confirmationIdentity = JSON.stringify([
    open ? 'open' : 'closed',
    kind,
    coachPersonId,
    memberPersonId,
    context?.seat_type ?? null,
    selectedSlot?.start_at ?? null,
    selectedSlot?.end_at ?? null,
    context?.timezone ?? null,
    selectedAppointmentCount ?? null,
    context?.cadence_weeks ?? null,
    calendarConflictEvidenceFingerprint(selectedCalendarConflict),
  ]);
  if (calendarConflictConfirmation.identity !== confirmationIdentity) {
    setCalendarConflictConfirmation({ identity: confirmationIdentity, confirmed: false });
  }
  const calendarConflictConfirmed =
    calendarConflictConfirmation.identity === confirmationIdentity &&
    calendarConflictConfirmation.confirmed;
  const calendarConflictOverrideRequired = Boolean(selectedCalendarConflict?.override_required);

  useEffect(() => {
    if (
      !operation ||
      operation.status === 'pending' ||
      completedOperation.current === operation.id
    ) {
      return;
    }
    completedOperation.current = operation.id;
    submitting.current = false;
    setRequestUnresolved(false);
    idempotency.markTerminal();
    if (operation.status === 'confirmed') {
      setConfirmedComplete(true);
      setMessage(
        bookingConfirmation(
          kind,
          confirmedAppointment,
          selectedStart,
          context?.timezone,
          memberPersonId,
          selectedAppointmentCount,
          context?.cadence_weeks,
          context?.coach.display_name,
          context?.member.display_name
        )
      );
      onComplete();
    } else {
      const guidance = adminAppointmentErrorGuidance(undefined, operation.rejection);
      setErrorGuidance(guidance ?? null);
      setErrorMessage(
        guidance ? null : adminAppointmentErrorMessage(undefined, operation.rejection)
      );
    }
  }, [
    confirmedAppointment,
    context?.cadence_weeks,
    context?.coach.display_name,
    context?.member.display_name,
    context?.timezone,
    idempotency,
    kind,
    memberPersonId,
    onComplete,
    operation,
    selectedAppointmentCount,
    selectedStart,
  ]);

  const isPending =
    Boolean(operationId) &&
    (!operation || operation.id !== operationId || operation.status === 'pending');
  const isLocked = requestUnresolved || mutation.isLoading || isPending;
  const recurrenceReady =
    kind === 'chemistry' ||
    Boolean(context?.cadence_weeks && selectedAppointmentCount && selectedAppointmentCount > 0);
  const canSchedule = Boolean(
    selectedStart &&
      context?.eligibility.eligible &&
      recurrenceReady &&
      (!calendarConflictOverrideRequired || calendarConflictConfirmed) &&
      !isLocked &&
      !confirmedComplete
  );

  async function handleSchedule() {
    if (!canSchedule || !context || !selectedSlot || submitting.current) return;
    let recurrence: { cadence_weeks: number; appointment_count: number } | null = null;
    if (kind === 'coaching') {
      if (
        !isPositiveInteger(context.cadence_weeks) ||
        !isPositiveInteger(selectedSlot.appointmentCount)
      ) {
        return;
      }
      recurrence = {
        cadence_weeks: context.cadence_weeks,
        appointment_count: selectedSlot.appointmentCount,
      };
    }
    submitting.current = true;
    setRequestUnresolved(true);
    setErrorMessage(null);
    setErrorGuidance(null);
    const commandInputs = {
      contract_version: 'appointment-admin-book.v1' as const,
      kind,
      coach_person_id: coachPersonId,
      member_person_id: memberPersonId,
      seat_type: context.seat_type,
      start_at: selectedSlot.start_at,
      viewer_timezone: context.timezone,
      recurrence,
      ...(calendarConflictOverrideRequired && calendarConflictConfirmed
        ? { calendar_conflict_override: true }
        : {}),
    };
    try {
      const result = await book({
        ...commandInputs,
        idempotency_key: idempotency.keyFor(commandInputs),
      }).unwrap();
      if (result.status === 'pending') {
        setConfirmedAppointment(Array.isArray(result.appointments) ? result.appointments[0] : null);
        setOperationId(result.operation_id);
        setPendingAppointmentIds(
          Array.isArray(result.appointments)
            ? result.appointments.map((appointment) => appointment.id)
            : []
        );
        setMessage('Scheduling changes are pending provider confirmation.');
      } else if (result.status === 'rejected') {
        submitting.current = false;
        setRequestUnresolved(false);
        idempotency.markTerminal();
        const guidance = adminAppointmentErrorGuidance(undefined, result.rejection);
        setErrorGuidance(guidance ?? null);
        setErrorMessage(
          guidance ? null : adminAppointmentErrorMessage(undefined, result.rejection)
        );
      } else {
        submitting.current = false;
        setRequestUnresolved(false);
        idempotency.markTerminal();
        setConfirmedComplete(true);
        const appointment = Array.isArray(result.appointments) ? result.appointments[0] : null;
        setConfirmedAppointment(appointment);
        setMessage(
          bookingConfirmation(
            kind,
            appointment,
            selectedStart,
            context.timezone,
            memberPersonId,
            selectedAppointmentCount,
            context.cadence_weeks,
            context.coach.display_name,
            context.member.display_name
          )
        );
        onComplete();
      }
    } catch (error) {
      submitting.current = false;
      setRequestUnresolved(false);
      if (isAdminAppointmentTerminalRejection(error)) idempotency.markTerminal();
      const guidance = adminAppointmentErrorGuidance(error);
      setErrorGuidance(guidance ?? null);
      setErrorMessage(guidance ? null : adminAppointmentErrorMessage(error));
      if (guidance?.title === 'Calendar conflict confirmation required') {
        setCalendarConflictConfirmation({ identity: confirmationIdentity, confirmed: false });
        await contextQuery.refetch();
      }
    }
  }

  return (
    <Dialog open={open} onClose={isLocked ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{kind === 'chemistry' ? 'Book Session' : 'Book Series'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {contextQuery.isLoading && <CircularProgress size={22} />}
        {contextErrorGuidance && (
          <Alert severity="error">
            <Typography>{contextErrorGuidance.title}</Typography>
            <Typography variant="body2">{contextErrorGuidance.detail}</Typography>
          </Alert>
        )}
        {contextUnavailable && kind === 'chemistry' && !contextErrorGuidance && (
          <Alert severity="error">
            <Typography>Scheduling context could not be loaded.</Typography>
            <Typography variant="body2">
              Retry, or go back without making changes. No scheduling changes were made.
            </Typography>
          </Alert>
        )}
        {schedulingGuard && (
          <Alert severity="warning">
            <Typography>{schedulingGuard.title}</Typography>
            <Typography variant="body2">{schedulingGuard.detail}</Typography>
          </Alert>
        )}
        {contextUnavailable && (
          <Button variant="outlined" onClick={() => contextQuery.refetch()}>
            Retry
          </Button>
        )}
        {context && !schedulingGuard && (
          <>
            <Typography color="text.secondary">
              {context.duration_minutes} minutes · {context.timezone}
            </Typography>
            <Typography>Coach: {context.coach.display_name}</Typography>
            <Typography>Member email: {context.member.email}</Typography>
            <Typography color="text.secondary">
              Google Meet · {context.duration_minutes} minutes · {context.timezone}
            </Typography>
            {kind === 'coaching' && (
              <>
                <Typography color="text.secondary">
                  {context.seat_type ? `${capitalize(context.seat_type)} coaching` : 'Coaching'} ·{' '}
                  {context.appointment_count ? `${context.appointment_count} sessions · ` : ''}
                  every {context.cadence_weeks} weeks
                </Typography>
                <Typography>
                  Coaching type:{' '}
                  {context.seat_type ? capitalize(context.seat_type) : 'Not available'}
                </Typography>
                <Typography>Cadence: Every {context.cadence_weeks} weeks</Typography>
                {(selectedAppointmentCount ?? context.appointment_count) && (
                  <Typography>
                    Authoritative sessions: {selectedAppointmentCount ?? context.appointment_count}
                  </Typography>
                )}
              </>
            )}
            {kind === 'coaching' && context.engagement_end_date && (
              <Typography>Engagement end: {formatISODate(context.engagement_end_date)}</Typography>
            )}
            {context.availability.days[0] && (
              <Typography color="text.secondary">
                {visibleDateRange(context.availability.days[0].date, visibleWeeks)}
              </Typography>
            )}
            <Alert severity="info">
              {kind === 'chemistry' ? (
                'Chemistry calls create one session. Confirm the member and selected time before scheduling.'
              ) : (
                <>
                  <Typography>
                    {selectedAppointmentCount
                      ? `${selectedAppointmentCount} sessions · every ${context.cadence_weeks} weeks`
                      : 'Select a time to confirm the series length.'}
                  </Typography>
                  <Typography variant="body2">Confirm the series before scheduling.</Typography>
                </>
              )}
            </Alert>
            {!context.eligibility.eligible && (
              <Alert severity="warning">{context.eligibility.reasons.join(' ')}</Alert>
            )}
            {slots.length === 0 ? (
              <Typography>No availability in this window.</Typography>
            ) : (
              <Box
                sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}
              >
                {slots.map((slot) => (
                  <Button
                    key={slot.start_at}
                    variant={selectedStart === slot.start_at ? 'contained' : 'outlined'}
                    onClick={() => setSelectedStart(slot.start_at)}
                    disabled={isLocked}
                  >
                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{slot.label}</span>
                      <Typography component="span" variant="caption" aria-hidden="true">
                        Local: {formatInstant(slot.start_at, context.timezone)}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Box>
            )}
            {selectedStart && (
              <Box>
                <Typography>Selected: {formatInstant(selectedStart, context.timezone)}</Typography>
                <Typography>
                  Local date: {formatInstantDate(selectedStart, context.timezone)}
                </Typography>
                {kind === 'coaching' && context.engagement_end_date && (
                  <Typography>
                    Engagement duration:{' '}
                    {engagementDuration(
                      selectedStart,
                      context.engagement_end_date,
                      context.timezone
                    )}
                  </Typography>
                )}
                {selectedCalendarConflict && (
                  <Alert
                    severity={calendarConflictOverrideRequired ? 'warning' : 'info'}
                    sx={{ mt: 2 }}
                  >
                    <Typography>
                      {selectedCalendarConflict.conflicting_occurrence_count} of{' '}
                      {selectedCalendarConflict.checked_occurrence_count} checked sessions overlap
                      selected calendars
                      {selectedCalendarConflict.first_occurrence_conflicting
                        ? ', including the first session.'
                        : '.'}
                    </Typography>
                    {selectedCalendarConflict.calendars.map((calendar) => (
                      <Typography
                        key={`${calendar.provider}:${calendar.connection_id}:${calendar.provider_calendar_id}`}
                        variant="body2"
                      >
                        {capitalize(calendar.provider)}: {calendar.calendar_summary}
                      </Typography>
                    ))}
                    {calendarConflictOverrideRequired && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={calendarConflictConfirmed}
                            onChange={(event) =>
                              setCalendarConflictConfirmation({
                                identity: confirmationIdentity,
                                confirmed: event.target.checked,
                              })
                            }
                          />
                        }
                        label="I understand these calendar conflicts and want to schedule anyway"
                      />
                    )}
                  </Alert>
                )}
              </Box>
            )}
            {visibleWeeks < 3 && (
              <Button variant="text" onClick={() => setVisibleWeeks((visibleWeeks + 1) as 2 | 3)}>
                Show More
              </Button>
            )}
          </>
        )}
        {message && (
          <Alert severity={isPending ? 'info' : 'success'}>
            {confirmedComplete && (
              <Typography sx={{ fontWeight: 600 }}>
                {kind === 'chemistry' ? 'Chemistry call booked!' : "You're scheduled for coaching!"}
              </Typography>
            )}
            <Typography variant="body2">{message}</Typography>
            {confirmedComplete && kind === 'chemistry' && (
              <Box sx={{ mt: 1 }}>
                <a href={MOMENTS_COACH_FAQS_URL} target="_blank" rel="noreferrer">
                  Learn More
                </a>
              </Box>
            )}
          </Alert>
        )}
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {errorGuidance && (
          <Alert severity="error">
            <Typography>{errorGuidance.title}</Typography>
            <Typography variant="body2">{errorGuidance.detail}</Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLocked}>
          {confirmedComplete ? 'Done' : 'Back'}
        </Button>
        {!schedulingGuard && !contextUnavailable && (
          <Button variant="contained" onClick={handleSchedule} disabled={!canSchedule}>
            {isLocked ? 'Scheduling…' : 'Schedule'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function engagementSchedulingGuard(eligibility: unknown, sourceUnavailable: boolean) {
  if (sourceUnavailable) return unavailableSchedulingGuard();
  if (typeof eligibility !== 'object' || eligibility === null || !('eligible' in eligibility)) {
    return unavailableSchedulingGuard();
  }
  if (!('reasons' in eligibility)) return unavailableSchedulingGuard();
  const reasons = eligibility.reasons;
  if (!Array.isArray(reasons)) return unavailableSchedulingGuard();
  if (eligibility.eligible === true)
    return reasons.length === 0 ? null : unavailableSchedulingGuard();
  const guidance = reasons.map((reason) => engagementSchedulingGuidance(reason)).find(Boolean);
  return guidance ?? unavailableSchedulingGuard();
}

function unavailableSchedulingGuard() {
  return ENGAGEMENT_SCHEDULING_GUIDANCE.member_engagement_unavailable;
}

function hasValidCoachingAvailability(context: AdminAppointmentSchedulingContext): boolean {
  return (
    Number.isInteger(context.cadence_weeks) &&
    Number(context.cadence_weeks) > 0 &&
    Array.isArray(context.availability?.days) &&
    context.availability.days.every(
      (day) =>
        Array.isArray(day.slots) &&
        (day.slots.length === 0 ||
          (day.enabled &&
            Number.isInteger(day.appointment_count) &&
            Number(day.appointment_count) > 0))
    )
  );
}

function calendarConflictEvidenceFingerprint(conflict: AdminCalendarConflict | null | undefined) {
  if (!conflict) return 'no-conflict';
  const calendars = conflict.calendars
    .map((calendar) => [
      calendar.provider,
      calendar.connection_id,
      calendar.provider_calendar_id,
      calendar.calendar_summary,
    ])
    .sort((left, right) => {
      const leftKey = JSON.stringify(left);
      const rightKey = JSON.stringify(right);
      return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
    });
  return JSON.stringify([
    conflict.checked_occurrence_count,
    conflict.conflicting_occurrence_count,
    conflict.first_occurrence_conflicting,
    conflict.override_required,
    calendars,
  ]);
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0;
}

function visibleDateRange(firstDate: string, weeks: number): string {
  const start = new Date(`${firstDate}T12:00:00Z`);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + weeks * 7 - 1);
  const startLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(start);
  const endLabel =
    start.getUTCMonth() === end.getUTCMonth()
      ? `${end.getUTCDate()}, ${end.getUTCFullYear()}`
      : new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(end);
  return `Showing ${startLabel}–${endLabel}`;
}

function bookingConfirmation(
  kind: AppointmentKind,
  appointment: AdminAppointment | null,
  selectedStart: string | null,
  timezone: string | undefined,
  memberPersonId: string,
  appointmentCount: number | undefined,
  cadenceWeeks: number | null | undefined,
  contextCoachName: string | undefined,
  contextMemberName: string | undefined
): string {
  const start = appointment?.current_schedule.start_at ?? selectedStart;
  const zone = appointment?.current_schedule.timezone ?? timezone;
  const member =
    appointment?.expected_identities.member.display_name ?? contextMemberName ?? memberPersonId;
  const coach =
    appointment?.expected_identities.coach.display_name ?? contextCoachName ?? 'the selected coach';
  if (!start || !zone) return 'Scheduling changes are confirmed.';
  const when = formatInstant(start, zone);
  if (kind === 'chemistry')
    return `Chemistry session confirmed for ${member} with ${coach}, beginning ${when} (${zone}).`;
  const coachingType = appointment?.seat_type
    ? `${capitalize(appointment.seat_type)} coaching`
    : 'Coaching';
  const cadence =
    appointmentCount && cadenceWeeks
      ? `, ${appointmentCount} sessions every ${cadenceWeeks} weeks`
      : '';
  return `${coachingType} confirmed for ${member} with ${coach}, beginning ${when}${cadence} (${zone}).`;
}

function formatISODate(date: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
}

function formatInstantDate(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  }).format(new Date(instant));
}

function engagementDuration(start: string, endDate: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  }).formatToParts(new Date(start));
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  const startDate = Date.UTC(value('year'), value('month') - 1, value('day'));
  const end = new Date(`${endDate}T00:00:00Z`).getTime();
  const weeks = Math.ceil((end - startDate) / (7 * 24 * 60 * 60 * 1000));
  return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
}

function formatInstant(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(instant));
}

function capitalize(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}

export default AdminAppointmentBookingDialog;
